<!--
  Contabilità — hub: selettore di periodo, striscia KPI e una card per COD,
  raggruppate in "Ricavi e partner" e "Compensi e provvigioni".
  Verde = ricavo, magenta = uscita.

  Si parte dal COD e dal mese; la scelta del partner o dell'agenzia è un
  passaggio in un dialog, perché le pagine di dettaglio lavorano su una
  entità per volta (/contabilita/codN/:id).

  ⚠️ SI SCEGLIE IL PARTNER, NON LA SEDE: il resoconto di un codice è
  dell'intero partner, con dentro tutte le sue sedi (le query filtrano su
  `partnerId`).

  ⚠️ COD7 SIGNIFICA DUE COSE DIVERSE. La rotta `/contabilita/cod7/:id` punta a
  `Cod4PageValet`, il pagamento valet della vecchia numerazione; la card COD7
  sono le provvigioni advisor.

  ⚠️ Un importo che non si può calcolare si mostra "—": dove le righe
  superano il tetto del backend la somma non viene mostrata (vedi
  contabilitaAPI.js). Su una pagina di contabilità un totale sbagliato ma
  credibile è peggio di un totale assente.
-->
<template>
  <div class="cnt">
    <div class="cnt__head">
      <h1 class="cnt__title">
        Contabilità
      </h1>
      <div class="cnt__sp" />
      <div class="period">
        <button
          type="button"
          class="period__nav"
          title="Mese precedente"
          @click="shiftMonth(-1)"
        >
          ‹
        </button>
        <span class="period__lbl">{{ periodLabel }}</span>
        <button
          type="button"
          class="period__nav"
          title="Mese successivo"
          @click="shiftMonth(1)"
        >
          ›
        </button>
      </div>
    </div>

    <!-- ================================ KPI ============================== -->
    <div class="kpis">
      <div
        v-for="k in kpis"
        :key="k.label"
        class="kpi"
        :class="`kpi--${k.tone}`"
      >
        <div class="kpi__lbl">
          {{ k.label }}
        </div>
        <div class="kpi__num">
          {{ k.value }}
        </div>
        <div class="kpi__sub">
          {{ k.sub }}
        </div>
      </div>
    </div>

    <!-- ========================== RICAVI E PARTNER ======================= -->
    <h2 class="cnt__sec">
      Ricavi e partner
    </h2>
    <div class="grid">
      <button
        v-for="c in revenueCods"
        :key="c.code"
        type="button"
        class="cod"
        @click="openCod(c)"
      >
        <span class="cod__top">
          <span
            class="cod__code"
            :class="`cod__code--${c.tone}`"
          >{{ c.code }}</span>
          <span class="cod__tt">{{ c.title }}</span>
        </span>
        <span class="cod__desc">{{ c.desc }}</span>
        <span class="cod__foot">
          <span class="cod__amt">
            {{ amountOf(c) }}
            <small class="cod__tag">{{ tagOf(c) }}</small>
          </span>
          <span class="cod__go">Apri →</span>
        </span>
      </button>
    </div>

    <!-- ======================= COMPENSI E PROVVIGIONI ==================== -->
    <h2 class="cnt__sec">
      Compensi e provvigioni
    </h2>
    <div class="grid">
      <button
        v-for="c in costCods"
        :key="c.code"
        type="button"
        class="cod"
        @click="openCod(c)"
      >
        <span class="cod__top">
          <span
            class="cod__code"
            :class="`cod__code--${c.tone}`"
          >{{ c.code }}</span>
          <span class="cod__tt">{{ c.title }}</span>
        </span>
        <span class="cod__desc">{{ c.desc }}</span>
        <span class="cod__foot">
          <span class="cod__amt">
            {{ amountOf(c) }}<span
              v-if="c.partial"
              class="cod__partial"
              title="Totale parziale"
            >*</span>
            <small class="cod__tag">{{ tagOf(c) }}</small>
          </span>
          <span
            v-if="pendingOf(c)"
            class="cod__liq"
          >{{ pendingOf(c) }}</span>
          <span
            v-else
            class="cod__go"
          >Apri →</span>
        </span>
      </button>
    </div>

    <p class="cnt__note">
      Gli importi sono la somma delle righe del mese selezionato. Dove compare
      «—» il totale non è ricavabile dai dati attuali, e dove compare «*» è
      parziale: il motivo è indicato sulla card.
    </p>

    <!-- Le pagine di dettaglio lavorano su UNA entità per volta
         (/contabilita/codN/:id): la scelta si fa qui. -->
    <el-dialog
      v-model="pickerOpen"
      :title="picker.title"
      width="460px"
    >
      <el-input
        v-model="pickerSearch"
        placeholder="Cerca…"
        clearable
        class="cnt__pickSearch"
      />
      <div
        v-loading="pickerLoading"
        class="cnt__pickList"
      >
        <button
          v-for="o in pickerFiltered"
          :key="o.id"
          type="button"
          class="cnt__pickItem"
          @click="choose(o)"
        >
          <span class="cnt__pickName">{{ o.label }}</span>
          <span
            v-if="o.sub"
            class="cnt__pickSub"
          >{{ o.sub }}</span>
        </button>
        <p
          v-if="!pickerLoading && !pickerFiltered.length"
          class="cnt__pickEmpty"
        >
          Nessun partner con movimenti in questo codice per
          {{ periodLabel }}.
        </p>
        <p
          v-if="!pickerLoading && pickerTruncated"
          class="cnt__pickEmpty"
        >
          ⚠ Il server ha troncato l'elenco dei movimenti: qui sono elencati
          <b>tutti</b> i partner, non solo quelli con qualcosa in questo mese.
        </p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import contabilitaAPI, { COD3_TUTTI } from '../../services/contabilitaAPI.js';
import partnerAPI from '../../services/partnerAPI.js';
import agencyAPI from '../../services/agencyAPI.js';
import { periodQuery } from '../../services/period.js';

// Un COD per card. `source` dice da dove arriva l'importo; `null` significa
// che non è calcolabile, e `why` spiega perché — compare al posto del tag.
const CODS = [
  {
    code: 'COD1', group: 'rev', tone: 'rev', route: 'cod1',
    title: 'Abbonamento mensile partner',
    desc: 'Canoni mensili dei partner attivi.',
    source: 'cod1', unit: 'sedi con canone',
    entity: 'partner',
  },
  {
    code: 'COD2', group: 'rev', tone: 'rev', route: 'cod2',
    title: 'Vendita su finanziamento',
    desc: 'Pacchetti valet venduti a rate: rata mensile del cliente.',
    source: 'cod2', unit: 'pacchetti',
    entity: 'partner',
  },
  {
    code: 'COD3', group: 'rev', tone: 'rev', route: 'cod3',
    title: 'Riepilogo booking',
    // Il totale della card è il fatturato delle PRENOTAZIONI; dentro la pagina
    // ci sono anche gli spostamenti interni, con il loro totale a parte.
    desc: 'Prenotazioni del mese; nella pagina anche gli spostamenti interni.',
    source: 'cod3', unit: 'prenotazioni', pendingLabel: 'da fatturare',
    entity: 'partner',
  },
  {
    code: 'COD4', group: 'rev', tone: 'rev', route: 'cod4',
    title: 'Provvigioni booking',
    desc: 'Provvigioni sui booking per partner.',
    source: 'cod4', unit: 'provvigioni',
    entity: 'partner',
  },
  {
    code: 'COD5', group: 'rev', tone: 'neutral', route: 'cod5',
    title: 'Transazioni in app',
    desc: 'Incassi dei clienti tramite app.',
    source: 'cod5', unit: 'transazioni',
    entity: 'partner',
  },
  {
    code: 'COD6', group: 'cost', tone: 'cost', route: 'cod6',
    title: 'Compensi driver',
    desc: 'Retribuzioni di driver e jolly.',
    source: 'cod6', unit: 'driver',
    // pagina di periodo: l'agenzia è un filtro dentro, non una scelta prima
    entity: null, direct: true,
    // Il totale copre pickup, transfer, consegne e spostamenti interni, più i
    // minuti aggiuntivi.
  },
  {
    code: 'COD7', group: 'cost', tone: 'cost', route: 'cod7',
    title: 'Provvigioni advisor',
    desc: 'Provvigioni degli advisor sui partner.',
    source: 'cod7', unit: 'advisor', pendingLabel: 'da liquidare',
    entity: null, direct: true,
  },
  {
    code: 'COD8', group: 'cost', tone: 'cost', route: 'cod8',
    title: 'Affitto mezzi',
    desc: 'Costi dei mezzi affittati dai fornitori (Transfer).',
    source: 'cod8', unit: 'fornitori', pendingLabel: 'da saldare',
    entity: null, direct: true,
  },
];

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

export default {
  name: 'ContabilitaPage',

  setup() {
    const router = useRouter();
    const now = new Date();
    const year = ref(now.getFullYear());
    const month = ref(now.getMonth() + 1);

    // risultati per COD: { amount, count, pending, truncated }
    const totals = ref({});
    const loading = ref(false);

    const periodLabel = computed(() => `${MONTHS[month.value - 1]} ${year.value}`);
    const revenueCods = computed(() => CODS.filter((c) => c.group === 'rev'));
    const costCods = computed(() => CODS.filter((c) => c.group === 'cost'));

    // Con i centesimi: la card deve coincidere con il totale della pagina.
    const euro = (n) =>
      new Intl.NumberFormat('it-IT', {
        style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(n || 0);

    const load = async () => {
      loading.value = true;
      const sources = CODS.filter((c) => c.source).map((c) => c.source);
      const res = await Promise.allSettled(
        sources.map((s) => contabilitaAPI[s](year.value, month.value))
      );
      const next = {};
      res.forEach((r, i) => {
        next[sources[i]] = r.status === 'fulfilled'
          ? r.value
          : { amount: null, count: null, pending: null, error: true };
      });
      totals.value = next;
      loading.value = false;
    };

    const dataOf = (c) => (c.source ? totals.value[c.source] : null);

    const amountOf = (c) => {
      if (!c.source) return '—';
      const d = dataOf(c);
      if (!d) return '…';
      if (d.error || d.truncated || d.amount === null) return '—';
      return euro(d.amount);
    };

    // Sotto l'importo: il conteggio, oppure il motivo per cui manca.
    const tagOf = (c) => {
      if (!c.source) return c.why;
      const d = dataOf(c);
      if (!d) return '';
      if (d.error) return 'dato non disponibile';
      if (d.truncated) return `troppe righe (${d.count}) per sommarle qui`;
      if (c.partial) return `${d.count} ${c.unit} · ${c.partial}`;
      return `${d.count} ${c.unit}`;
    };

    const pendingOf = (c) => {
      const d = dataOf(c);
      if (!d || !c.pendingLabel || !d.pending) return '';
      return `${d.pending} ${c.pendingLabel}`;
    };

    // La striscia in alto riusa i totali già caricati: niente chiamate in più.
    const kpis = computed(() => {
      const cod6 = totals.value.cod6;
      const cod7 = totals.value.cod7;
      const cod8 = totals.value.cod8;
      const val = (d) => (d && !d.truncated && d.amount !== null ? euro(d.amount) : '—');
      return [
        {
          label: 'Abbonamenti partner', tone: 'rev', value: val(totals.value.cod1),
          sub: 'COD1 · canoni mensili sedi',
        },
        {
          label: 'Valet venduti', tone: 'rev', value: val(totals.value.cod2),
          sub: 'COD2 · pacchetti su finanziamento',
        },
        {
          label: 'Compensi driver', tone: 'cost', value: val(cod6),
          sub: cod6?.count ? `COD6 · ${cod6.count} driver` : 'COD6 · ore × tariffa agenzia',
        },
        {
          label: 'Provvigioni e mezzi', tone: 'cost',
          value: cod7 && cod8 && !cod7.truncated
            ? euro((cod7.amount || 0) + (cod8.amount || 0))
            : '—',
          sub: 'COD7 + COD8',
        },
      ];
    });

    const shiftMonth = (delta) => {
      let m = month.value + delta;
      let y = year.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      month.value = m;
      year.value = y;
      load();
    };

    // ---------------------------------------------- scelta dell'entità ---
    const pickerOpen = ref(false);
    const pickerLoading = ref(false);
    const pickerSearch = ref('');
    const pickerOptions = ref([]);
    // elenco dei movimenti troncato dal server: il filtro non è affidabile e
    // si mostrano tutti i partner, dicendolo
    const pickerTruncated = ref(false);
    const picker = ref({ title: '', cod: null });

    const pickerFiltered = computed(() => {
      const t = pickerSearch.value.trim().toLowerCase();
      if (!t) return pickerOptions.value;
      return pickerOptions.value.filter(
        (o) => `${o.label} ${o.sub || ''}`.toLowerCase().includes(t)
      );
    });

    const openCod = async (c) => {
      // COD senza pagina: meglio dirlo che portare su quella sbagliata.
      if (c.unavailable) {
        ElMessage.info(c.unavailable);
        return;
      }
      // pagine già di periodo: si entra senza scegliere un'entità.
      // Il periodo scelto qui viaggia nella query: senza, la pagina figlia
      // ripartirebbe dal mese corrente e mostrerebbe numeri di un altro mese.
      if (c.direct) {
        router.push({
          path: `/contabilita/${c.route}`,
          query: periodQuery(year.value, month.value),
        });
        return;
      }
      picker.value = {
        cod: c,
        title: c.entity === 'agency'
          ? `${c.code} · scegli l'agenzia`
          : `${c.code} · scegli il partner`,
      };
      pickerSearch.value = '';
      pickerOptions.value = [];
      pickerOpen.value = true;
      pickerLoading.value = true;
      try {
        if (c.entity === 'agency') {
          const r = await agencyAPI.getAgencies();
          pickerOptions.value = (r.data?.data ?? r.data ?? []).map((a) => ({
            id: a.id,
            label: a.company_name,
            sub: [a.name, a.surname].filter(Boolean).join(' '),
          }));
        } else {
          // Il resoconto è del PARTNER intero, con dentro tutte le sue sedi.
          //
          // Si mostrano SOLO i partner che nel periodo hanno movimenti in
          // QUESTO codice: presentarli tutti obbliga a provarli a uno a uno
          // per scoprire che il mese è vuoto.
          const [r, movimenti] = await Promise.all([
            partnerAPI.getPartnersPaged({ limit: 500 }),
            contabilitaAPI.partnersWithData(c.source, year.value, month.value),
          ]);
          pickerTruncated.value = movimenti.truncated;

          const tutti = r.rows.map((p) => {
            const n = movimenti.counts[p.id] ?? 0;
            // COD3: anche gli spostamenti interni, contati a parte. Un
            // partner che nel mese ha fatto solo spostamenti compare con
            // quelli, non con «0 prenotazioni».
            const altro = movimenti.extra?.[p.id];
            const conteggio = [n ? `${n} ${c.unit}` : null, altro?.label]
              .filter(Boolean).join(' · ');
            return {
              id: p.id,
              label: p.company_name,
              count: n + (altro?.n ?? 0),
              sub: [
                p.piva ? `P.IVA ${p.piva}` : null,
                movimenti.ids
                  ? conteggio
                  : (p.headquarters?.length
                    ? `${p.headquarters.length} sed${p.headquarters.length === 1 ? 'e' : 'i'}`
                    : null),
              ].filter(Boolean).join(' · '),
            };
          });

          // `ids` null = elenco troncato: non si filtra, si mostrano tutti.
          pickerOptions.value = movimenti.ids
            ? tutti.filter((o) => movimenti.ids.has(o.id))
              .sort((a, b) => b.count - a.count)
            : tutti;
          // COD3: in testa la vista di tutti i partner insieme, per i ricavi
          // generali del mese.
          if (c.code === 'COD3') {
            pickerOptions.value.unshift({
              id: COD3_TUTTI,
              label: 'Tutti i partner',
              sub: 'Ricavi del mese di tutti i partner insieme',
            });
          }
        }
      } catch (e) {
        console.error('Caricamento elenco fallito:', e);
        ElMessage.error('Impossibile caricare l\'elenco');
      } finally {
        pickerLoading.value = false;
      }
    };

    const choose = (o) => {
      pickerOpen.value = false;
      // idem: l'id identifica il partner, la query il periodo
      router.push({
        path: `/contabilita/${picker.value.cod.route}/${o.id}`,
        query: periodQuery(year.value, month.value),
      });
    };

    onMounted(load);

    return {
      year, month, loading, periodLabel, revenueCods, costCods, kpis,
      amountOf, tagOf, pendingOf, shiftMonth, openCod,
      pickerOpen, pickerLoading, pickerSearch, pickerFiltered, picker, choose,
      pickerTruncated,
    };
  },
};
</script>

<style scoped>
.cnt { padding: 4px 0 32px; }
.cnt__sp { flex: 1; }

.cnt__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.cnt__title { margin: 0; font-size: 22px; color: var(--text-primary); }

.period {
  display: flex; align-items: center; gap: 2px; padding: 3px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px;
}
.period__nav {
  width: 32px; height: 32px; border: 0; background: transparent;
  border-radius: 8px; cursor: pointer; font-size: 16px;
  color: var(--text-secondary); font-family: inherit;
}
.period__nav:hover { background: var(--bg-tertiary); }
.period__lbl {
  padding: 0 12px; font-weight: 700; font-variant-numeric: tabular-nums;
  min-width: 130px; text-align: center; color: var(--text-primary);
}

/* ---------------------------------- KPI --------------------------------- */
.kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px;
}
@media (max-width: 820px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .kpis { grid-template-columns: 1fr; } }
.kpi {
  position: relative; overflow: hidden; padding: 14px 16px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
}
.kpi::before {
  content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px;
}
.kpi--rev::before { background: var(--state-ok); }
.kpi--cost::before { background: #a62e82; }
.kpi__lbl {
  font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 600;
}
.kpi__num {
  font-size: 24px; font-weight: 750; letter-spacing: -0.02em; margin-top: 5px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.kpi__sub { font-size: 11.5px; color: var(--text-tertiary); margin-top: 3px; }

/* -------------------------------- sezioni ------------------------------- */
.cnt__sec {
  font-size: 13px; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--text-tertiary); margin: 0 0 10px;
}
.grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 24px;
}
@media (max-width: 900px) { .grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px) { .grid { grid-template-columns: 1fr; } }

/* ------------------------------- card COD ------------------------------- */
.cod {
  display: flex; flex-direction: column; text-align: left; cursor: pointer;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden; font-family: inherit;
  padding: 0; transition: border-color var(--transition-fast);
}
.cod:hover { border-color: var(--brand-violet); }
.cod__top {
  display: flex; align-items: flex-start; gap: 11px; padding: 15px 16px 12px;
}
.cod__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px; font-weight: 700; color: #fff; padding: 3px 8px;
  border-radius: 6px; flex: none; background: var(--state-muted);
}
.cod__code--rev { background: var(--state-ok); }
.cod__code--cost { background: #a62e82; }
.cod__code--neutral { background: var(--state-muted); }
.cod__tt { font-weight: 700; font-size: 14.5px; color: var(--text-primary); }
.cod__desc {
  color: var(--text-tertiary); font-size: 12.5px; padding: 0 16px;
  line-height: 1.45; min-height: 34px;
}
.cod__foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 16px; margin-top: 12px;
  border-top: 1px solid var(--border-color); background: var(--bg-secondary);
}
.cod__amt {
  font-weight: 750; font-size: 16px; font-variant-numeric: tabular-nums;
  color: var(--text-primary);
}
.cod__tag {
  display: block; font-size: 11px; color: var(--text-tertiary); font-weight: 600;
}
.cod__partial {
  color: var(--state-attn); font-weight: 800; margin-left: 2px;
}
.cod__go {
  color: var(--brand-violet); font-weight: 650; font-size: 12.5px; white-space: nowrap;
}
.cod__liq {
  font-size: 11px; font-weight: 700; white-space: nowrap;
  color: var(--state-attn); background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line); border-radius: 20px; padding: 2px 9px;
}

.cnt__pickSearch { margin-bottom: 10px; }
.cnt__pickList { max-height: 340px; overflow-y: auto; }
.cnt__pickItem {
  display: block; width: 100%; text-align: left; cursor: pointer;
  padding: 9px 11px; border-radius: 8px; background: transparent;
  border: 1px solid transparent; font-family: inherit;
}
.cnt__pickItem:hover {
  background: var(--bg-secondary); border-color: var(--border-color);
}
.cnt__pickName { display: block; font-weight: 600; color: var(--text-primary); }
.cnt__pickSub { display: block; font-size: 12px; color: var(--text-tertiary); }
.cnt__pickEmpty { font-size: 13px; color: var(--text-tertiary); padding: 8px 11px; }

.cnt__note {
  margin: 4px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}
</style>
